import { DeleteIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { SolidActionButton } from '@components/Buttons'
import { VisitModality } from '@hb/shared'
import { useTimeLogConfig } from '../../../hooks/backend/timeLog/useTimeLogConfig'
import { PauseIcon } from '@icons/PausePlay'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useField, useForm } from 'react-final-form'
import { useTimerState } from './TimerStateContext'

const TimeMinutesInput = ({ fieldName }: { fieldName: string }) => {
  const { input } = useField<number | undefined>(fieldName, { subscription: { value: true } })
  const form = useForm()
  const { runningTimers, pauseFunctionsRef } = useTimerState()
  const isTimerRunning = runningTimers[fieldName]
  const isInvalid = !isTimerRunning && !input.value

  if (isTimerRunning) {
    return (
      <Flex py={1} gap={3} bg="orange.100" pr={1} pl={3} borderRadius="full" align="center">
        <Text
          fontSize="sm"
          position="relative"
          bottom="1px"
          color="orange.600"
          fontWeight={500}
          whiteSpace="nowrap">
          Timer running
        </Text>
        <Button
          size="xs"
          borderRadius="full"
          bg="orange.200"
          _hover={{ bg: 'orange.300', color: 'orange.50' }}
          color="orange.700"
          leftIcon={<PauseIcon boxSize={4} />}
          variant="ghost"
          onClick={() => pauseFunctionsRef.current[fieldName]?.()}>
          PAUSE
        </Button>
      </Flex>
    )
  }

  return (
    <>
      <NumberInput
        size="sm"
        w="98px"
        min={0}
        max={480}
        precision={0}
        isInvalid={isInvalid}
        bg="white"
        _dark={{ bg: 'gray.900' }}
        borderRadius={6}
        value={input.value ?? ''}
        onChange={v => form.change(fieldName, v === '' ? undefined : Number(v))}>
        <NumberInputField _invalid={{ borderColor: 'red.300' }} borderRadius={6} />
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
      <Text fontSize="sm" whiteSpace="nowrap" color="gray.500" ml={1} w="24px">
        min
      </Text>
    </>
  )
}

const TimeField = ({ label, fieldName }: { label: string; fieldName: string }) => {
  const { input } = useField<number | undefined>(fieldName, { subscription: { value: true } })
  const { runningTimers } = useTimerState()
  const isInvalid = !runningTimers[fieldName] && !input.value

  return (
    <Flex align="center" justify="space-between" w="100%" py={1} gap={1}>
      <Text fontSize="sm" flex={1} minW={0} color={isInvalid ? 'red.500' : undefined}>
        {label}
      </Text>
      <TimeMinutesInput fieldName={fieldName} />
    </Flex>
  )
}

const OTHER_FIELD = 'data.timeWorkLog.otherMinutes'

const OtherTimeField = () => {
  const form = useForm()
  const { input: labelInput } = useField<string | undefined>('data.timeWorkLog.otherLabel', {
    subscription: { value: true },
  })
  const { setShowOtherTime, focusOtherLabelRef } = useTimerState()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    focusOtherLabelRef.current = () => inputRef.current?.focus()
    return () => {
      focusOtherLabelRef.current = null
    }
  }, [focusOtherLabelRef])

  const handleDelete = useCallback(() => {
    setShowOtherTime(false)
    form.change(OTHER_FIELD, undefined)
    form.change('data.timeWorkLog.otherLabel', undefined)
  }, [form, setShowOtherTime])

  return (
    <Flex align="center" w="100%" py={1} gap={1}>
      <Box flex={1} minW={0}>
        <Input
          ref={inputRef}
          size="sm"
          w="100%"
          placeholder="Describe other time..."
          borderRadius={6}
          bg="white"
          maxW="350px"
          _dark={{ bg: 'gray.900' }}
          value={labelInput.value ?? ''}
          onChange={e => form.change('data.timeWorkLog.otherLabel', e.target.value || undefined)}
        />
      </Box>
      <Tooltip bg="red.600" color="red.50" placement="left" hasArrow label="Delete Other Time">
        <IconButton
          aria-label="Delete Other Time"
          variant="ghost"
          color="red.600"
          icon={<DeleteIcon />}
          _hover={{ bg: 'red.100', color: 'red.700' }}
          size="sm"
          onClick={handleDelete}
        />
      </Tooltip>
      <TimeMinutesInput fieldName={OTHER_FIELD} />
    </Flex>
  )
}

const useTimeWorkLogValues = () => {
  const fields = [
    'faceToFaceMinutes',
    'patientHistoryMinutes',
    'labReviewMinutes',
    'externalRecordsMinutes',
    'orderingMinutes',
    'chartingMinutes',
    'counselingMinutes',
    'careCoordinationMinutes',
    'preVisitContactMinutes',
    'otherMinutes',
  ] as const

  const values = fields.map(f => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { input } = useField<number | undefined>(`data.timeWorkLog.${f}`, {
      subscription: { value: true },
    })
    return input.value
  })

  return useMemo(
    () =>
      Object.fromEntries(fields.map((f, i) => [f, values[i]])) as Record<
        (typeof fields)[number],
        number | undefined
      >,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    values,
  )
}

export interface TimeWorkLogPanelHandle {
  open: (confirmLabel?: string) => void
}

interface TimeWorkLogPanelProps {
  onConfirm: () => void
}

// §2.10: Persistent footer panel for Time & Work Log.
// Slides up above Save/Authenticate when the provider taps Save Draft or Authenticate.
export const TimeWorkLogPanel = forwardRef<TimeWorkLogPanelHandle, TimeWorkLogPanelProps>(
  ({ onConfirm }, ref) => {
    const form = useForm()
    const [isOpen, setIsOpen] = useState(false)
    const [confirmLabel, setConfirmLabel] = useState('Save Draft')
    const { fieldLabels } = useTimeLogConfig()
    const {
      showOtherTime: showOther,
      setShowOtherTime: setShowOther,
      openTimePanelForOtherRef,
      focusOtherLabelRef,
    } = useTimerState()

    useImperativeHandle(ref, () => {
      const openForOther = () => {
        setConfirmLabel('Authenticate')
        setShowOther(true)
        setIsOpen(true)
        setTimeout(() => focusOtherLabelRef.current?.(), 100)
      }
      openTimePanelForOtherRef.current = openForOther
      return {
        open: (label = 'Save Draft') => {
          setConfirmLabel(label)
          setIsOpen(true)
        },
      }
    }, [focusOtherLabelRef, openTimePanelForOtherRef, setShowOther])

    const { input: modalityInput } = useField<VisitModality | undefined>(
      'data.visitLocation.modality',
      { subscription: { value: true } },
    )
    const { input: externalRecordsInput } = useField<boolean | undefined>(
      'data.externalRecordsReviewed',
      { subscription: { value: true } },
    )
    const { input: patientEducationInput } = useField<string[] | undefined>(
      'data.patientEducation',
      {
        subscription: { value: true },
      },
    )
    const { input: referralsInput } = useField<string[] | undefined>('data.referrals', {
      subscription: { value: true },
    })
    const { input: labReviewConfirmationInput } = useField<unknown>(
      'data.labs.labReviewConfirmation',
      { subscription: { value: true } },
    )
    const { input: preVisitContactMinutesInput } = useField<number | undefined>(
      'data.preVisitContact.contactMinutes',
      { subscription: { value: true } },
    )

    const isAudioOnly = modalityInput.value === 'telehealth-audio-only'
    const hasLabReview = !!labReviewConfirmationInput.value
    const hasExternalRecords = externalRecordsInput.value === true
    const hasPatientEducation =
      Array.isArray(patientEducationInput.value) && patientEducationInput.value.length > 0
    const hasReferral = Array.isArray(referralsInput.value) && referralsInput.value.length > 0
    // §2.16: Pre-visit contact row auto-appears when contactMinutes is filled.
    const hasPreVisitContact =
      typeof preVisitContactMinutesInput.value === 'number' && preVisitContactMinutesInput.value > 0

    const timeValues = useTimeWorkLogValues()

    const total = useMemo(
      () =>
        [
          timeValues.faceToFaceMinutes,
          timeValues.patientHistoryMinutes,
          timeValues.labReviewMinutes,
          timeValues.externalRecordsMinutes,
          timeValues.orderingMinutes,
          timeValues.chartingMinutes,
          timeValues.counselingMinutes,
          timeValues.careCoordinationMinutes,
          timeValues.preVisitContactMinutes,
          timeValues.otherMinutes,
        ]
          .filter((v): v is number => typeof v === 'number')
          .reduce((sum, v) => sum + v, 0),
      [timeValues],
    )

    const allFilled = useMemo(() => {
      const required: (number | undefined)[] = [
        ...(!isAudioOnly ? [timeValues.faceToFaceMinutes] : []),
        timeValues.patientHistoryMinutes,
        timeValues.chartingMinutes,
        ...(hasLabReview ? [timeValues.labReviewMinutes] : []),
        ...(hasExternalRecords ? [timeValues.externalRecordsMinutes] : []),
        ...(hasReferral ? [timeValues.orderingMinutes] : []),
        ...(hasPatientEducation ? [timeValues.counselingMinutes] : []),
        ...(hasReferral ? [timeValues.careCoordinationMinutes] : []),
        ...(hasPreVisitContact ? [timeValues.preVisitContactMinutes] : []),
        ...(showOther ? [timeValues.otherMinutes] : []),
      ]
      return required.every(v => typeof v === 'number' && v > 0)
    }, [
      isAudioOnly,
      hasLabReview,
      hasExternalRecords,
      hasReferral,
      hasPatientEducation,
      hasPreVisitContact,
      showOther,
      timeValues,
    ])

    // §2.16: When the panel opens, pre-populate preVisitContactMinutes from the clinical
    // pre-visit contact field if the time log field isn't already filled.
    useEffect(() => {
      if (!isOpen) return
      const contactMins = preVisitContactMinutesInput.value
      if (
        typeof contactMins === 'number' &&
        contactMins > 0 &&
        !timeValues.preVisitContactMinutes
      ) {
        form.change('data.timeWorkLog.preVisitContactMinutes', contactMins)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // Keep totalMinutes in sync as an auto-calculated field.
    useEffect(() => {
      form.change('data.timeWorkLog.totalMinutes', total || undefined)
    }, [total, form])

    const handleConfirm = useCallback(() => {
      if (!allFilled) return
      setIsOpen(false)
      onConfirm()
    }, [allFilled, onConfirm])

    return (
      <Collapse
        style={{ maxHeight: '100%', position: 'absolute', width: '100%', bottom: 0 }}
        in={isOpen}
        animateOpacity>
        <Box
          borderTop="2px solid"
          borderColor="pink.200"
          bg="pink.50"
          maxH="100%"
          overflowY="auto"
          _dark={{ bg: 'pink.900', borderColor: 'pink.700' }}
          px={4}
          py={3}>
          <Text
            fontWeight={600}
            fontSize="sm"
            mb={0}
            color="blue.800"
            _dark={{ color: 'blue.200' }}>
            Time & Work Log
          </Text>
          <VStack
            spacing={0}
            align="stretch"
            divider={<Divider borderColor="gray.400" _dark={{ borderColor: 'gray.600' }} />}>
            {!isAudioOnly && (
              <TimeField
                label={fieldLabels.faceToFaceMinutes}
                fieldName="data.timeWorkLog.faceToFaceMinutes"
              />
            )}
            <TimeField
              label={fieldLabels.patientHistoryMinutes}
              fieldName="data.timeWorkLog.patientHistoryMinutes"
            />
            <TimeField
              label={fieldLabels.chartingMinutes}
              fieldName="data.timeWorkLog.chartingMinutes"
            />
            {hasLabReview && (
              <TimeField
                label={fieldLabels.labReviewMinutes}
                fieldName="data.timeWorkLog.labReviewMinutes"
              />
            )}
            {hasExternalRecords && (
              <TimeField
                label={fieldLabels.externalRecordsMinutes}
                fieldName="data.timeWorkLog.externalRecordsMinutes"
              />
            )}
            {hasReferral && (
              <TimeField
                label={fieldLabels.orderingMinutes}
                fieldName="data.timeWorkLog.orderingMinutes"
              />
            )}
            {hasPatientEducation && (
              <TimeField
                label={fieldLabels.counselingMinutes}
                fieldName="data.timeWorkLog.counselingMinutes"
              />
            )}
            {hasReferral && (
              <TimeField
                label={fieldLabels.careCoordinationMinutes}
                fieldName="data.timeWorkLog.careCoordinationMinutes"
              />
            )}
            {hasPreVisitContact && (
              <TimeField
                label={fieldLabels.preVisitContactMinutes}
                fieldName="data.timeWorkLog.preVisitContactMinutes"
              />
            )}
            {showOther && <OtherTimeField />}
            {!showOther && (
              <Box py={2}>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="pink"
                  onClick={() => setShowOther(true)}>
                  + Add other time
                </Button>
              </Box>
            )}
          </VStack>
          <Divider mb={3} />
          <Flex justify="space-between" align="center">
            <HStack>
              <Text fontSize="sm" fontWeight={600}>
                Total Visit Time:
              </Text>
              <Text
                fontSize="sm"
                fontWeight={700}
                color={total === 0 ? 'red.500' : 'green.600'}
                _dark={{ color: total === 0 ? 'red.300' : 'green.300' }}>
                {total} min
              </Text>
            </HStack>
            <HStack>
              <Button size="sm" variant="ghost" colorScheme="gray" onClick={() => setIsOpen(false)}>
                Back
              </Button>
              <SolidActionButton size="sm" isDisabled={!allFilled} onClick={handleConfirm}>
                {confirmLabel}
              </SolidActionButton>
            </HStack>
          </Flex>
          {!allFilled && (
            <Text fontSize="sm" color="red.500" mt={1}>
              All visible time categories require a value before saving.
            </Text>
          )}
        </Box>
      </Collapse>
    )
  },
)
